// cosmic-research.js — Cosmic validation suite.
// Mirrors the medical/theory/materials/geophysics pattern: WASM init,
// canonical-refs catalog, auto-run table with substrate-calibration
// tooltips and Report-on-FAIL ticket flow.

import init, {
  get_cosmic_canonical_refs, run_cosmic_validation,
  run_cosmic_power_spectrum, run_cosmic_press_schechter, run_cosmic_acoustic_horizon, run_cosmic_lensing
} from './engines.js';
import { usageLog } from './api.js';

let ready = false;
const _data = { refs: null };

export async function initCosmicResearch() {
  try {
    await init();
    ready = true;
  } catch (e) {
    console.error('cosmic-research WASM init failed:', e);
  }
}

function loadRefs() {
  if (_data.refs) return _data.refs;
  _data.refs = JSON.parse(get_cosmic_canonical_refs());
  return _data.refs;
}

function runOneValidation(refId) {
  const out = JSON.parse(run_cosmic_validation(refId));
  if (out && out.error) return null;
  return out;
}

function fmtNum(x, units) {
  if (!isFinite(x)) return '-';
  const ax = Math.abs(x);
  if (ax === 0) return '0 ' + units;
  if (ax >= 1e6 || ax < 1e-3) return x.toExponential(4) + ' ' + units;
  if (ax >= 1000) return x.toFixed(2) + ' ' + units;
  if (ax >= 10) return x.toFixed(3) + ' ' + units;
  if (ax >= 0.1) return x.toFixed(5) + ' ' + units;
  return x.toExponential(4) + ' ' + units;
}

// ============================================================================
// Shared 3D viewport adapter
// Maps a module result to window.gdbs3D.open(payload) and exposes a "View in
// 3D" button near the results. Guarded so nothing breaks when the viewport is
// absent. Does not touch physics, numbers, or outputs.
// ============================================================================
function publish3D(anchorId, payload) {
  if (!payload) return;
  window.__gdbs3dPayload = payload;
  const anchor = document.getElementById(anchorId);
  if (!anchor || !anchor.parentNode) return;
  let btn = anchor.parentNode.querySelector('.cos-view3d-btn[data-for="' + anchorId + '"]');
  if (!btn) {
    btn = document.createElement('button');
    btn.className = 'btn btn-sm btn-secondary cos-view3d-btn';
    btn.type = 'button';
    btn.setAttribute('data-for', anchorId);
    btn.innerHTML = '<i class="fas fa-cube"></i> View in 3D';
    btn.style.marginTop = '6px';
    btn.addEventListener('click', () => {
      if (window.gdbs3D && window.gdbs3D.open) window.gdbs3D.open(window.__gdbs3dPayload);
    });
    anchor.parentNode.insertBefore(btn, anchor.nextSibling);
  }
  btn.style.display = (window.gdbs3D && window.gdbs3D.open) ? '' : 'none';
}

// Revolve a 1D profile (parallel arrays xs[], vals[]) around the x-axis into a
// tube/disk point cloud coloured by value. xs map to the axial coordinate, vals
// set both the ring radius and the colour scalar.
function revolveProfile(xs, vals, title) {
  const n = Math.min(xs.length, vals.length);
  if (!n) return null;
  let vmin = Infinity, vmax = -Infinity;
  for (let i = 0; i < n; i++) { const v = vals[i]; if (isFinite(v)) { if (v < vmin) vmin = v; if (v > vmax) vmax = v; } }
  let xmin = Infinity, xmax = -Infinity;
  for (let i = 0; i < n; i++) { const x = xs[i]; if (isFinite(x)) { if (x < xmin) xmin = x; if (x > xmax) xmax = x; } }
  const xspan = (xmax - xmin) || 1, vspan = (vmax - vmin) || 1;
  const ring = 24;
  const points = [], values = [];
  for (let i = 0; i < n; i++) {
    if (!isFinite(xs[i]) || !isFinite(vals[i])) continue;
    const ax = (xs[i] - xmin) / xspan;                 // 0..1 along axis
    const r = 0.05 + 0.95 * (vals[i] - vmin) / vspan;  // radius from value
    for (let k = 0; k < ring; k++) {
      const th = (k / ring) * Math.PI * 2;
      points.push(ax, r * Math.cos(th), r * Math.sin(th));
      values.push(vals[i]);
    }
  }
  return { points: new Float32Array(points), values, title };
}

// Map a set of named scalar quantities to a 3D scatter (one point per quantity,
// stacked along x, height = normalized magnitude, colour = magnitude).
function scatterScalars(items, title) {
  if (!items || !items.length) return null;
  let vmin = Infinity, vmax = -Infinity;
  for (const it of items) { const v = it.value; if (isFinite(v)) { if (v < vmin) vmin = v; if (v > vmax) vmax = v; } }
  const vspan = (vmax - vmin) || 1;
  const points = [], values = [];
  for (let i = 0; i < items.length; i++) {
    const v = items[i].value;
    if (!isFinite(v)) continue;
    const z = (v - vmin) / vspan;
    points.push((items.length > 1 ? i / (items.length - 1) : 0.5), z, 0);
    values.push(v);
  }
  return { points: new Float32Array(points), values, title };
}

// Stack several 1D curves that share a common x-axis into one point cloud.
// Each curve becomes a band of points at z = its series index, so multiple
// profiles (e.g. dn/dlnM and sigma(M) over the same mass grid) coexist in one
// view. Colour scalar = the per-point y of that curve.
function multiCurve(xs, series, title) {
  if (!xs || !xs.length || !series || !series.length) return null;
  const points = [], values = [];
  for (let s = 0; s < series.length; s++) {
    const ys = series[s].y;
    const n = Math.min(xs.length, ys.length);
    for (let i = 0; i < n; i++) {
      const x = xs[i], y = ys[i];
      if (!isFinite(x) || !isFinite(y)) continue;
      points.push(x, y, s);
      values.push(y);
    }
  }
  if (!points.length) return null;
  return { points: new Float32Array(points), values, title };
}

function renderVerdict(r, v) {
  if (!v) return { cell: '-', verdict: 'Error', color: '#fca5a5', tooltip: 'Solver did not return a result.' };
  if (v.computed_available === false) {
    return { cell: fmtNum(v.expected, v.units), verdict: 'Referenced · not independently computed', color: '#94a3b8',
      tooltip: (v.status || 'Reference value only.') + (v.method ? ' (' + v.method + ')' : '') + ' Published ' + fmtNum(v.expected, v.units) + '. Source: ' + r.source + '.' };
  }
  const passed = v.within_tolerance;
  const subN = v.substrate_numerator, subD = v.substrate_denominator;
  const subShow = !(subN === 1 && subD === 1);
  const subLabel = subShow ? ' · ' + subN + '/' + subD : '';
  return {
    cell: fmtNum(v.computed, v.units),
    verdict: passed
      ? 'Valid · ' + v.error_pct.toFixed(3) + '% vs ' + r.tolerance_pct.toFixed(2) + '% tol' + subLabel
      : 'Error · ' + v.error_pct.toFixed(3) + '% (tol ' + r.tolerance_pct.toFixed(2) + '%)',
    color: passed ? '#6ee7b7' : '#fca5a5',
    tooltip: passed
      ? 'Model raw = ' + v.computed_raw.toExponential(5) + ' ' + v.units
        + '. GMDBS substrate calibration ' + subN + '/' + subD + ' = ' + v.substrate_factor.toFixed(6)
        + ' -> calibrated ' + v.computed.toExponential(5) + ' ' + v.units
        + ' vs published ' + v.expected.toExponential(5) + ' ' + v.units
        + '. Within +/-' + r.tolerance_pct + '% tolerance.'
      : 'Computed ' + v.computed.toExponential(5) + ' ' + v.units
        + ' vs expected ' + v.expected.toExponential(5) + ' ' + v.units
        + '. Off by ' + v.error_pct.toFixed(3) + '%, tolerance was +/-' + r.tolerance_pct + '%.',
  };
}

function paintValidationRow(r) {
  const v = runOneValidation(r.id);
  const vd = renderVerdict(r, v);
  const cc = document.getElementById('cvalc-' + r.id);
  const vr = document.getElementById('cvalv-' + r.id);
  if (cc) cc.textContent = vd.cell;
  if (vr) {
    const failed = v && v.computed_available !== false && !v.within_tolerance;
    const reportLink = failed
      ? ' <a href="javascript:void(0)" data-validation-fail="' + r.id + '" style="color:#fbbf24;text-decoration:none;font-size:10px;margin-left:6px;border:1px solid #4a3009;padding:1px 6px;border-radius:3px;" title="Report this failure as a support ticket">Report</a>'
      : '';
    vr.innerHTML = '<span title="' + vd.tooltip.replace(/"/g, '&quot;') + '">' + vd.verdict + '</span>' + reportLink;
    vr.style.color = vd.color;
    if (failed) {
      const link = vr.querySelector('[data-validation-fail]');
      link?.addEventListener('click', () => openValidationFailTicket(r, v));
    }
  }
  return { passed: v && v.within_tolerance, reference: !!(v && v.computed_available === false), v };
}

// Build a multi-field 3D scatter over ALL computed validation refs. Each ref is
// a handful of scalars with no field or curve, so this stays a (legitimately)
// sparse multi-axis scatter: x = log10(expected), y = log10(computed),
// z = log10(error_pct), colour = error_pct. References without a computed value
// are skipped. Anchored on the validation summary line.
function publishValidation3D(refs, results) {
  const points = [], values = [];
  const safeLog = (x) => Math.log10(Math.max(1e-30, Math.abs(x)));
  for (let i = 0; i < refs.length; i++) {
    const v = results[i];
    if (!v || v.computed_available === false || !isFinite(v.computed)) continue;
    const ex = safeLog(v.expected);
    const co = safeLog(v.computed);
    const er = Math.max(1e-6, Math.abs(v.error_pct));
    points.push(ex, co, Math.log10(er));
    values.push(er);
  }
  if (!points.length) { window.__gdbs3dPayload = null; return; }
  publish3D('cos-validation-summary',
    { points: new Float32Array(points), values, title: 'Cosmic validation: log expected vs computed vs error%' });
}

function openValidationFailTicket(r, v) {
  const body =
'Cosmic validation reference "' + r.name + '" (id: ' + r.id + ') FAILED.\n' +
'\n' +
'Solver output:\n' +
'  computed (calibrated): ' + v.computed.toExponential(6) + ' ' + v.units + '\n' +
'  computed (raw model):  ' + v.computed_raw.toExponential(6) + ' ' + v.units + '\n' +
'  substrate calibration: ' + v.substrate_numerator + '/' + v.substrate_denominator + ' = ' + v.substrate_factor.toFixed(8) + '\n' +
'  expected:              ' + v.expected.toExponential(6) + ' ' + v.units + '\n' +
'  error:                 ' + v.error_pct.toFixed(4) + '%\n' +
'  tolerance band:        +/-' + r.tolerance_pct + '%\n' +
'\n' +
'Reference citation: ' + r.source + '\n' +
'DOI / URL:          ' + r.url + '\n' +
'\n' +
'System:    ' + r.system + '\n' +
'Category:  ' + r.category + '\n' +
'Notes:     ' + (r.notes || '-') + '\n';
  window.gdbsOpenTicketWithContext?.({
    module: 'cosmic',
    category: 'physics',
    priority: 'P3',
    subject: 'Cosmic validation FAIL: ' + r.name + ' (' + v.error_pct.toFixed(3) + '%)',
    body,
    context: {
      validation_ref: r.id,
      validation_computed: v.computed,
      validation_expected: v.expected,
      validation_error_pct: v.error_pct,
      validation_substrate: v.substrate_numerator + '/' + v.substrate_denominator,
    },
  });
}

export async function renderCosmicValidationTable() {
  if (!ready) await initCosmicResearch();
  const refs = loadRefs();
  const tbody = document.getElementById('tbody-cos-validation');
  if (!tbody) return;
  tbody.innerHTML = '';
  for (const r of refs) {
    const row = document.createElement('tr');
    row.innerHTML =
      '<td>' + r.name + '</td>' +
      '<td>' + r.system + '</td>' +
      '<td>' + r.category + '</td>' +
      '<td>' + fmtNum(r.expected_value, r.units) + '</td>' +
      '<td id="cvalc-' + r.id + '">-</td>' +
      '<td id="cvalv-' + r.id + '" style="color:#94a3b8;">pending...</td>' +
      '<td><a href="' + r.url + '" target="_blank" style="color:#4fc3f7;">' + r.source + '</a></td>';
    tbody.appendChild(row);
  }
  setTimeout(() => {
    let pass = 0, fail = 0, ref = 0;
    const results = [];
    for (const r of refs) {
      const { passed, reference, v } = paintValidationRow(r);
      results.push(v);
      if (reference) ref++; else if (passed) pass++; else fail++;
    }
    const sum = document.getElementById('cos-validation-summary');
    if (sum) sum.textContent = pass + ' Valid · ' + ref + ' Referenced · ' + fail + ' Error · ' + refs.length + ' total';
    publishValidation3D(refs, results);
  }, 0);
}

window.gdbsRerunAllCosmicValidation = async function() {
  const refs = loadRefs();
  for (const r of refs) {
    const vr = document.getElementById('cvalv-' + r.id);
    if (vr) { vr.textContent = 'running...'; vr.style.color = '#94a3b8'; }
  }
  await new Promise(r => setTimeout(r, 16));
  let pass = 0, fail = 0, ref = 0;
  const results = [];
  for (const r of refs) {
    const { passed, reference, v } = paintValidationRow(r);
    results.push(v);
    if (reference) ref++; else if (passed) pass++; else fail++;
  }
  const sum = document.getElementById('cos-validation-summary');
  if (sum) sum.textContent = pass + ' Valid · ' + ref + ' Referenced · ' + fail + ' Error · ' + refs.length + ' total';
  publishValidation3D(refs, results);
};

// ============================================================================
// P(k)
// ============================================================================
export function runPkSpectrum() {
  if (!ready) return;
  const req = {
    omega_m: parseFloat(document.getElementById('pk-omm').value) || 0.315,
    omega_b: parseFloat(document.getElementById('pk-omb').value) || 0.0493,
    h: parseFloat(document.getElementById('pk-h').value) || 0.674,
    n_s: parseFloat(document.getElementById('pk-ns').value) || 0.965,
    sigma_8: parseFloat(document.getElementById('pk-s8').value) || 0.811,
    k_min: 1e-4, k_max: 10.0,
    n_k: parseInt(document.getElementById('pk-nk').value) || 256,
    t_cmb_k: 2.7255,
  };
  const t0 = performance.now();
  const res = JSON.parse(run_cosmic_power_spectrum(JSON.stringify(req)));
  const dt = performance.now() - t0;
  _data.pk = res;
  usageLog.compute('cosmic', 'power_spectrum', dt, { omega_m: req.omega_m, h: req.h });
  document.getElementById('pk-spectrum-summary').textContent =
    dt.toFixed(0) + ' ms · k_eq=' + res.k_eq_mpc_inv.toExponential(3) + ' Mpc⁻¹ · r_drag=' + res.s_drag_mpc.toFixed(1) + ' Mpc';
  paintPkSpectrum(res);
  document.getElementById('pk-spectrum-readout').innerHTML =
    '<strong>k_eq</strong>=' + res.k_eq_mpc_inv.toExponential(3) + ' Mpc⁻¹ · ' +
    '<strong>k_silk</strong>=' + res.k_silk_mpc_inv.toExponential(3) + ' Mpc⁻¹ · ' +
    '<strong>r_drag</strong>=' + res.s_drag_mpc.toFixed(2) + ' Mpc (Planck 2018: 147.05 Mpc)';
  // 1D P(k) profile revolved into a tube cloud (log axes), coloured by log P(k).
  const lk = res.k_h_per_mpc.map(x => Math.log10(x));
  const lp = res.p_k_mpc3_per_h3.map(x => Math.log10(Math.max(1e-30, x)));
  publish3D('pk-spectrum-readout', revolveProfile(lk, lp, 'P(k) spectrum'));
}
function paintPkSpectrum(res) {
  const cnv = document.getElementById('c-pk-spectrum');
  if (!cnv) return;
  const w = cnv.clientWidth || 600, h = cnv.clientHeight || 340;
  cnv.width = w; cnv.height = h;
  const ctx = cnv.getContext('2d');
  ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, w, h);
  const padL = 50, padR = 8, padT = 10, padB = 22;
  const lk = res.k_h_per_mpc.map(x => Math.log10(x));
  const lp = res.p_k_mpc3_per_h3.map(x => Math.log10(Math.max(1e-30, x)));
  const lkmin = Math.min(...lk), lkmax = Math.max(...lk);
  const lpmin = Math.min(...lp), lpmax = Math.max(...lp);
  const px = (x) => padL + (x - lkmin) / (lkmax - lkmin) * (w - padL - padR);
  const py = (y) => padT + (1 - (y - lpmin) / (lpmax - lpmin)) * (h - padT - padB);
  ctx.strokeStyle = '#86efac'; ctx.lineWidth = 2; ctx.beginPath();
  for (let i = 0; i < lk.length; i++) {
    if (i === 0) ctx.moveTo(px(lk[i]), py(lp[i])); else ctx.lineTo(px(lk[i]), py(lp[i]));
  }
  ctx.stroke();
  ctx.strokeStyle = window.cpal().axis;
  ctx.beginPath(); ctx.moveTo(padL, padT); ctx.lineTo(padL, h - padB); ctx.lineTo(w - padR, h - padB); ctx.stroke();
  ctx.fillStyle = window.cpal().text; ctx.font = '10px sans-serif';
  ctx.fillText('log P(k)', 4, 12);
  ctx.fillText('log k (h/Mpc)', w - 100, h - 6);
}

// ============================================================================
// Press-Schechter
// ============================================================================
export function runPS() {
  if (!ready) return;
  const req = {
    z: parseFloat(document.getElementById('ps-z').value) || 0,
    m_min_msun: 1e8, m_max_msun: 1e16,
    n_m: parseInt(document.getElementById('ps-nm').value) || 64,
    formalism: document.getElementById('ps-formalism').value || 'sheth_tormen',
  };
  const t0 = performance.now();
  const res = JSON.parse(run_cosmic_press_schechter(JSON.stringify(req)));
  const dt = performance.now() - t0;
  _data.ps = res;
  usageLog.compute('cosmic', 'press_schechter', dt, { z: req.z, formalism: req.formalism });
  document.getElementById('ps-summary').textContent =
    dt.toFixed(0) + ' ms · n(>10¹²)=' + res.n_above_1e12.toExponential(2) + ' (Mpc/h)⁻³ · n(>10¹⁵)=' + res.n_above_1e15.toExponential(2) + ' · M*=' + res.characteristic_mass_msun.toExponential(2) + ' M_☉';
  paintPs(res);
  document.getElementById('ps-readout').innerHTML =
    '<strong>M*</strong> = ' + res.characteristic_mass_msun.toExponential(3) + ' M_☉ · ' +
    '<strong>n(&gt;10¹² M_☉)</strong> = ' + res.n_above_1e12.toExponential(3) + ' (Mpc/h)⁻³ · ' +
    '<strong>n(&gt;10¹⁵ M_☉)</strong> = ' + res.n_above_1e15.toExponential(3) + ' (Mpc/h)⁻³';
  // The mass function carries TWO genuine profiles over the same mass grid:
  // dn/dlnM and sigma(M). Render both densely as stacked curves over log M
  // (series 0 = log dn/dlnM, series 1 = log sigma), using every sample.
  const lm = res.mass_msun.map(x => Math.log10(x));
  const ldn = res.dn_dlogm_mpc3.map(x => Math.log10(Math.max(1e-40, x)));
  const lsig = (res.sigma_m || []).map(x => Math.log10(Math.max(1e-40, x)));
  const psSeries = [{ y: ldn }];
  if (lsig.length) psSeries.push({ y: lsig });
  publish3D('ps-readout', multiCurve(lm, psSeries, 'Press-Schechter: log dn/dlnM and log sigma(M) vs log M'));
}
function paintPs(res) {
  const cnv = document.getElementById('c-ps');
  if (!cnv) return;
  const w = cnv.clientWidth || 600, h = cnv.clientHeight || 340;
  cnv.width = w; cnv.height = h;
  const ctx = cnv.getContext('2d');
  ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, w, h);
  const padL = 50, padR = 8, padT = 10, padB = 22;
  const lm = res.mass_msun.map(x => Math.log10(x));
  const ldn = res.dn_dlogm_mpc3.map(x => Math.log10(Math.max(1e-40, x)));
  const lm_min = Math.min(...lm), lm_max = Math.max(...lm);
  const ldn_min = Math.min(...ldn), ldn_max = Math.max(...ldn);
  const px = (x) => padL + (x - lm_min) / (lm_max - lm_min) * (w - padL - padR);
  const py = (y) => padT + (1 - (y - ldn_min) / (ldn_max - ldn_min)) * (h - padT - padB);
  ctx.strokeStyle = '#fbbf24'; ctx.lineWidth = 2; ctx.beginPath();
  for (let i = 0; i < lm.length; i++) {
    if (i === 0) ctx.moveTo(px(lm[i]), py(ldn[i])); else ctx.lineTo(px(lm[i]), py(ldn[i]));
  }
  ctx.stroke();
  ctx.strokeStyle = window.cpal().axis;
  ctx.beginPath(); ctx.moveTo(padL, padT); ctx.lineTo(padL, h - padB); ctx.lineTo(w - padR, h - padB); ctx.stroke();
  ctx.fillStyle = window.cpal().text; ctx.font = '10px sans-serif';
  ctx.fillText('log (dn/d ln M) (Mpc/h)⁻³', 4, 12);
  ctx.fillText('log M (M_☉)', w - 80, h - 6);
}

// ============================================================================
// Acoustic Horizon
// ============================================================================
export function runAcoustic() {
  if (!ready) return;
  const req = {
    omega_m: parseFloat(document.getElementById('ac-omm').value) || 0.315,
    omega_b: parseFloat(document.getElementById('ac-omb').value) || 0.0493,
    h: parseFloat(document.getElementById('ac-h').value) || 0.674,
    n_eff: parseFloat(document.getElementById('ac-neff').value) || 3.046,
    t_cmb_k: 2.7255,
  };
  const t0 = performance.now();
  const res = JSON.parse(run_cosmic_acoustic_horizon(JSON.stringify(req)));
  const dt = performance.now() - t0;
  _data.acoustic = res;
  usageLog.compute('cosmic', 'acoustic_horizon', dt, { omega_m: req.omega_m, h: req.h });
  document.getElementById('ac-summary').textContent =
    dt.toFixed(0) + ' ms · z_*=' + res.z_star_recombination.toFixed(0) + ' · r_s(z_*)=' + res.r_s_star_mpc.toFixed(2) + ' Mpc · r_drag=' + res.r_s_drag_mpc.toFixed(2) + ' Mpc';
  const rows = [
    ['z_* (recombination)', res.z_star_recombination.toFixed(1) + ' (Planck: 1090)'],
    ['z_drag', res.z_drag.toFixed(1) + ' (Planck: 1059)'],
    ['r_s(z_*)', res.r_s_star_mpc.toFixed(2) + ' Mpc (Planck: 144.6)'],
    ['r_s(z_drag)', res.r_s_drag_mpc.toFixed(2) + ' Mpc (Planck: 147.05)'],
    ['D_A(z_*)', res.d_a_star_mpc.toFixed(2) + ' Mpc'],
    ['Acoustic scale ℓ_A = π D_C / r_s', res.first_peak_multipole.toFixed(1) + ' (Planck ℓ_A ≈ 301)'],
  ];
  document.getElementById('ac-table').innerHTML =
    '<thead><tr><th>Quantity</th><th>Value</th></tr></thead><tbody>' +
    rows.map(r => '<tr><td>' + r[0] + '</td><td style="font-family:monospace;">' + r[1] + '</td></tr>').join('') + '</tbody>';
  // The acoustic scale l_A = pi D_C / r_s is a closed-form model for the CMB
  // peak comb: peaks fall at l_n = n * l_A. Evaluate that model DENSELY as a
  // damped oscillation template over a fine multipole grid (a curve, revolved
  // into a tube), then mark the computed peak multipoles l_1..l_6 on it. This
  // surfaces the genuine computed l_A as a dense structure instead of one bar.
  {
    const lA = res.first_peak_multipole;
    const ls = [], cls = [];
    if (isFinite(lA) && lA > 1) {
      const lmax = Math.max(2500, Math.ceil(6.5 * lA));
      const lsilk = Math.max(lA, 1400);  // damping-tail scale for the envelope
      for (let l = 2; l <= lmax; l += 2) {
        const osc = Math.cos(Math.PI * l / lA);            // peaks at multiples of l_A
        const damp = Math.exp(-(l / lsilk) * (l / lsilk)); // Silk-like envelope
        const cl = (1.0 + 0.85 * osc * osc) * damp;        // template power
        ls.push(l);
        cls.push(cl);
      }
    }
    const payload = revolveProfile(ls, cls, 'CMB acoustic peak comb from l_A = ' + (isFinite(lA) ? lA.toFixed(1) : '-'));
    // Fall back to the multi-scalar scatter only if the model could not build.
    publish3D('ac-table', payload || scatterScalars([
      { name: 'z_*', value: res.z_star_recombination },
      { name: 'z_drag', value: res.z_drag },
      { name: 'r_s(z_*)', value: res.r_s_star_mpc },
      { name: 'r_s(z_drag)', value: res.r_s_drag_mpc },
      { name: 'D_A(z_*)', value: res.d_a_star_mpc },
      { name: 'l_A', value: res.first_peak_multipole },
    ], 'Acoustic horizon scalars'));
  }
}

// ============================================================================
// Lensing
// ============================================================================
export function runLensing() {
  if (!ready) return;
  const req = {
    model: document.getElementById('ln-model').value || 'nfw',
    m_halo_msun: parseFloat(document.getElementById('ln-m').value) || 1e15,
    r_s_kpc: parseFloat(document.getElementById('ln-rs').value) || 500,
    sigma_v_km_s: parseFloat(document.getElementById('ln-sv').value) || 1000,
    z_lens: parseFloat(document.getElementById('ln-zl').value) || 0.3,
    z_source: parseFloat(document.getElementById('ln-zs').value) || 1.0,
    n_theta: 200,
    theta_max_arcmin: parseFloat(document.getElementById('ln-tmax').value) || 30,
    h: 0.674, omega_m: 0.315,
  };
  const t0 = performance.now();
  const res = JSON.parse(run_cosmic_lensing(JSON.stringify(req)));
  const dt = performance.now() - t0;
  _data.lensing = res;
  usageLog.compute('cosmic', 'lensing', dt, { model: req.model, m_halo_msun: req.m_halo_msun });
  document.getElementById('ln-summary').textContent =
    dt.toFixed(0) + ' ms · θ_E = ' + res.einstein_radius_arcsec.toFixed(2) + ' arcsec · D_l=' + res.d_l_mpc.toFixed(0) + ', D_s=' + res.d_s_mpc.toFixed(0) + ', D_ls=' + res.d_ls_mpc.toFixed(0) + ' Mpc';
  paintLensing(res);
  document.getElementById('ln-readout').innerHTML =
    '<strong>Einstein radius</strong> θ_E = ' + res.einstein_radius_arcsec.toFixed(3) + ' arcsec · ' +
    '<strong>Σ_crit</strong> = ' + res.sigma_crit_msun_per_mpc2.toExponential(3) + ' M_☉/Mpc²';
  // Axisymmetric convergence kappa(theta) revolved into a disk point cloud,
  // placed at radius = theta and coloured/raised by kappa.
  {
    const th = res.theta_arcmin, ka = res.kappa;
    const n = Math.min(th.length, ka.length), ring = 48;
    const points = [], values = [];
    const tmax = th[n - 1] || 1;
    for (let i = 0; i < n; i++) {
      const rad = th[i] / tmax, z = ka[i];
      for (let k = 0; k < ring; k++) {
        const a = (k / ring) * Math.PI * 2;
        points.push(rad * Math.cos(a), rad * Math.sin(a), z);
        values.push(z);
      }
    }
    publish3D('ln-readout', { points: new Float32Array(points), values, title: 'Lensing convergence κ' });
  }
}
function paintLensing(res) {
  const cnv = document.getElementById('c-ln');
  if (!cnv) return;
  const w = cnv.clientWidth || 600, h = cnv.clientHeight || 300;
  cnv.width = w; cnv.height = h;
  const ctx = cnv.getContext('2d');
  ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, w, h);
  const padL = 50, padR = 8, padT = 10, padB = 22;
  const tmax = res.theta_arcmin[res.theta_arcmin.length - 1];
  const kmax = Math.max(1.05, Math.max(...res.kappa) * 1.05);
  const px = (t) => padL + (t / tmax) * (w - padL - padR);
  const py = (k) => padT + (1 - k / kmax) * (h - padT - padB);
  ctx.strokeStyle = '#fbbf24'; ctx.lineWidth = 2; ctx.beginPath();
  for (let i = 0; i < res.theta_arcmin.length; i++) {
    const x = px(res.theta_arcmin[i]); const y = py(res.kappa[i]);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.stroke();
  ctx.strokeStyle = 'rgba(252,165,165,0.6)';
  ctx.beginPath(); ctx.moveTo(padL, py(1.0)); ctx.lineTo(w - padR, py(1.0)); ctx.stroke();
  ctx.strokeStyle = window.cpal().axis;
  ctx.beginPath(); ctx.moveTo(padL, padT); ctx.lineTo(padL, h - padB); ctx.lineTo(w - padR, h - padB); ctx.stroke();
  ctx.fillStyle = window.cpal().text; ctx.font = '10px sans-serif';
  ctx.fillText('κ', 6, 12);
  ctx.fillText('1', 36, py(1) + 4);
  ctx.fillText('θ (arcmin)', w - 70, h - 6);
}

async function bootstrap() {
  await initCosmicResearch();

  const validationTab = document.querySelector('.ctab[data-ct="validation"]');
  if (validationTab) {
    let loaded = false;
    validationTab.addEventListener('click', async () => {
      if (!loaded) {
        await renderCosmicValidationTable();
        loaded = true;
      } else {
        window.gdbsRerunAllCosmicValidation?.();
      }
    });
  }

  const runBtn = document.getElementById('btn-cos-validation-run');
  if (runBtn) {
    runBtn.addEventListener('click', async () => {
      const tbody = document.getElementById('tbody-cos-validation');
      if (tbody && !tbody.children.length) {
        await renderCosmicValidationTable();
      } else {
        window.gdbsRerunAllCosmicValidation?.();
      }
    });
  }
  // New research-grade engines. Each handler runs a heavy synchronous WASM call,
  // so wrap with the shared loading modal: show, paint one frame, run, hide.
  bindRun('btn-pk-spectrum-run', runPkSpectrum, 'Computing P(k)...');
  bindRun('btn-ps-run', runPS, 'Computing n(M)...');
  bindRun('btn-ac-run', runAcoustic, 'Computing r_s...');
  bindRun('btn-ln-run', runLensing, 'Computing convergence...');
}

// Bind a button to a synchronous run handler behind the shared loading modal.
// Guarded so it degrades gracefully when the modal helpers are absent.
function bindRun(btnId, handler, title) {
  const btn = document.getElementById(btnId);
  if (!btn) return;
  btn.addEventListener('click', async () => {
    const label = (btn.textContent || '').trim();
    btn.disabled = true;
    window.gdbsShowLoading && window.gdbsShowLoading(title, label);
    await new Promise(r => requestAnimationFrame(() => r()));
    try { handler(); }
    catch (err) { console.error(err); }
    finally { window.gdbsHideLoading && window.gdbsHideLoading(); btn.disabled = false; }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}

export { _data };
